import { Column, TablePaginated, TablePaginatedOptions } from '@acceso/design-system';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { RenderActionButtons } from '@/components/RenderActionButtons';
import { StateActiveInTable } from '@/components/StateActiveInTable/StateActiveInTable';
import { TableRow } from '@/components/TableRow';
import { TableRowInfo } from '@/components/TableRowInfo/TableRowInfo';
import { TableRowThumbnail } from '@/components/TableRowThumbnail/TableRowThumbnail';
import { TableUpdatedBy } from '@/components/TableUpdatedBy';
import { CHANNEL_MEDIA_TYPE_TO_ICON } from '@/features/broadcasters/constants/icons';
import { useChannelMediaTypeOptions } from '@/features/broadcasters/hooks/useChannelMediaTypeOptions';
import { useChannelOptions } from '@/features/broadcasters/hooks/useChannelOptions';
import { useChannelScopeOptions } from '@/features/broadcasters/hooks/useChannelScopeOptions';
import { ChannelMediaTypeEnum, ChannelScopeEnum, ChannelType } from '@/features/broadcasters/types';
import { useLocationCodeToLabel } from '@/features/locations/hooks/useGetLocationLabelFromCode';
import { useLocationOptions } from '@/features/locations/hooks/useLocationOptions';
import { useUserOptions } from '@/features/users/hooks/useUserOptions';
import { useStateOptions } from '@/hooks/useStateOptions';
import { State } from '@/types';
import { createTableFilterOptions } from '@/utils/table-filters';

import { useParentChannelFilters } from '../../stores/parent-channel-filters';

interface ParentBroadcastersTableProps {
  items: ChannelType[];
  totalItems: number;
  activeItem: ChannelType | null;
  onUpdate?: (parentBroadcasters: ChannelType) => void;
  onDuplicate?: (parentBroadcasters: ChannelType) => void;
  onDelete?: (parentBroadcasters: ChannelType) => void;
  onSelect?: (parentBroadcasters: ChannelType) => void;
  handleRemoveUserDetailView?: () => void;
  setData: Dispatch<SetStateAction<ChannelType | null>>;
  isFetching?: boolean;
}

export function ParentBroadcastersTable({
  items,
  totalItems,
  activeItem,
  onUpdate,
  onDuplicate,
  onDelete,
  onSelect,
  isFetching,
}: ParentBroadcastersTableProps) {
  const { t } = useTranslation();
  const { setFilters, paginationValues, setPage, setRows, setSortBy, setSearchQuery } =
    useParentChannelFilters();
  const channelMediaTypeOptions = useChannelMediaTypeOptions();
  const channelOptions = useChannelOptions({
    isParentChannel: true,
  });
  const channelScopeOptions = useChannelScopeOptions();
  const { regionOptions, subregionOptions } = useLocationOptions();
  const userOptions = useUserOptions();
  const locationCodeToLabel = useLocationCodeToLabel();

  const options: Partial<TablePaginatedOptions> = {
    pagination: {
      changePage: setPage,
      changeRows: setRows,
      currentPage: paginationValues.currentPage,
      itemsPerPage: paginationValues.rowsPerPage,
    },
    setSortBy,
    sortBy: paginationValues.sortBy,
    onSearchChange: setSearchQuery,
    filters: [
      {
        name: 'media_type',
        placeholder: t('Soporte'),
        options: createTableFilterOptions({
          options: channelMediaTypeOptions,
          allOptionLabel: t('Cualquier soporte'),
        }),
        onChange: value => {
          setFilters({
            mediaTypeIn: [value as unknown as ChannelMediaTypeEnum],
          });
        },
      },
      {
        name: 'broadcaster',
        placeholder: t('Emisora'),
        options: createTableFilterOptions({
          options: channelOptions,
          allOptionLabel: t('Cualquier emisora'),
        }),
        onChange: value => {
          setFilters({
            idIn: [value as unknown as string],
          });
        },
      },
      {
        name: 'scope',
        placeholder: t('Ámbito'),
        options: createTableFilterOptions({
          options: channelScopeOptions,
          allOptionLabel: t('Cualquier ámbito'),
        }),
        onChange: value => {
          setFilters({
            scope: value as unknown as ChannelScopeEnum,
          });
        },
      },
      {
        name: 'region',
        placeholder: t('Región'),
        options: createTableFilterOptions({
          options: regionOptions,
          allOptionLabel: t('Cualquier región'),
        }),
        onChange: value => {
          setFilters({
            regionCode: value as unknown as string,
          });
        },
      },
      {
        name: 'subregion',
        placeholder: t('Subregión'),
        options: createTableFilterOptions({
          options: subregionOptions,
          allOptionLabel: t('Cualquier subregión'),
        }),
        onChange: value => {
          setFilters({
            subregionCode: value as unknown as string,
          });
        },
      },
      {
        name: 'state',
        placeholder: t('Estado'),
        options: createTableFilterOptions({
          options: useStateOptions(),
          allOptionLabel: t('Cualquier estado'),
        }),
        onChange: value => {
          setFilters({
            state: value as unknown as State,
          });
        },
      },
      {
        name: 'updated_by',
        placeholder: t('Actualizado por'),
        options: createTableFilterOptions({
          options: userOptions,
          allOptionLabel: t('Actualizado por cualquiera'),
        }),
        onChange: value => {
          setFilters({
            updatedByIn: [value as unknown as string],
          });
        },
      },
    ],
    searchBarPlaceholder: t('Buscar por nombre de emisora o ID AV'),
  };

  const columns: Column<ChannelType>[] = useMemo(
    () => [
      {
        id: 'logo',
        title: t('Logo'),
        onColumnClick: item => onSelect?.(item),
        render: item => {
          return <TableRowThumbnail source={item.parameters?.logo_presigned_url} />;
        },
      },
      {
        id: 'name',
        title: t('Emisora'),
        sortable: true,
        onColumnClick: item => onSelect?.(item),
        render: item => {
          const Icon = CHANNEL_MEDIA_TYPE_TO_ICON[item.media_type];
          return (
            <TableRowInfo showTooltip title={item.name} description={item.short_name} icon={Icon} />
          );
        },
      },
      {
        id: 'scope',
        title: t('Ámbito'),
        sortable: true,
        render: item => {
          return <TableRow content={item.scope} />;
        },
      },
      {
        id: 'av_code',
        title: t('ID AV (Legacy)'),
        sortable: true,
        render: item => {
          return <TableRow content={item.av_code || ''} />;
        },
      },
      {
        id: 'country_code',
        title: t('Localización'),
        sortable: true,
        render: item => {
          return (
            <>
              <TableRow
                content={locationCodeToLabel[item.country_code]}
                className="!font-semibold"
              />
              <TableRow content={item.region_code ? locationCodeToLabel[item.region_code] : '-'} />
            </>
          );
        },
      },
      {
        id: 'state',
        title: t('Estado'),
        sortable: true,
        render: item => {
          return <StateActiveInTable status={item.state} />;
        },
      },
      {
        id: 'updated_by',
        title: t('Actualizado por'),
        sortable: true,
        render: item => {
          return <TableUpdatedBy item={item} />;
        },
      },
      {
        id: 'actions',
        title: '',
        className: 'items-end',
        width: '5%',
        render: item => (
          <RenderActionButtons
            item={item}
            onUpdate={() => onUpdate?.(item)}
            onDuplicate={() => onDuplicate?.(item)}
            onDelete={() => onDelete?.(item)}
          />
        ),
      },
    ],
    [t, onSelect, onUpdate, onDelete]
  );

  return (
    <TablePaginated
      options={options}
      columns={columns}
      data={items}
      totalItems={totalItems}
      activeItem={activeItem}
      isFetching={isFetching}
      ignoreEmpty
    />
  );
}
